import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Button,
  OutlineButton,
  Input,
  Select,
  TextArea,
  FormGroup,
  Label,
  FlexContainer,
  Grid,
  ErrorText
} from '../../styles/StyledComponents';
import { useAuth } from '../../context/AuthContext';
import { Project, ProjectStatus, ProjectType, UserRole, AssignedStaff, User } from '../../types';
import apiService from '../../services/api';
import NotificationService from '../../services/NotificationService';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const FormSection = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  color: var(--secondary-color);
  border-bottom: 2px solid var(--light-gray);
  padding-bottom: 0.5rem;
  margin-bottom: 1rem;
`;

const BudgetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const BudgetCard = styled(Card)`
  padding: 1rem;
  
  .budget-label {
    font-weight: 600;
    color: var(--secondary-color);
    margin-bottom: 0.5rem;
  }
  
  .budget-input {
    width: 100%;
  }
`;

const TotalBudget = styled.div`
  background: var(--light-gray);
  padding: 1rem;
  border-radius: var(--border-radius);
  text-align: center;
  margin-top: 1rem;
  
  .total-label {
    font-weight: 600;
    color: var(--secondary-color);
    margin-bottom: 0.5rem;
  }
  
  .total-amount {
    font-size: 1.5rem;
    font-weight: 600;
    color: var(--primary-color);
  }
`;

const StaffSection = styled.div`
  margin-bottom: 2rem;
`;

const StaffCard = styled.div`
  background: white;
  border: 1px solid var(--light-gray);
  border-radius: var(--border-radius);
  padding: 1rem;
  margin-bottom: 1rem;
  
  .staff-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }
  
  .staff-info {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }
`;

const AddStaffForm = styled.div`
  background: var(--light-gray);
  padding: 1rem;
  border-radius: var(--border-radius);
  margin-bottom: 1rem;
  
  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr auto;
    gap: 1rem;
    align-items: end;
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }
`;

const RemoveButton = styled(Button)`
  background-color: var(--danger-color);
  padding: 0.5rem 1rem;
  
  &:hover {
    background-color: #c82333;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  color: var(--primary-color);
`;

const UserSelectContainer = styled.div`
  position: relative;
  
  .user-info {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.25rem;
    font-size: 0.9rem;
    color: var(--dark-gray);
  }
  
  .user-role {
    background: var(--light-gray);
    padding: 0.25rem 0.5rem;
    border-radius: 0.25rem;
    font-size: 0.8rem;
  }
`;

const ErrorMessage = styled.div`
  background: var(--danger-color);
  color: white;
  padding: 0.75rem;
  border-radius: var(--border-radius);
  margin-bottom: 1rem;
  font-size: 0.9rem;
`;

const SuccessMessage = styled.div`
  background: var(--success-color);
  color: white;
  padding: 0.75rem;
  border-radius: var(--border-radius);
  margin-bottom: 1rem;
  font-size: 0.9rem;
`;

const RefreshButton = styled(Button)`
  background: var(--secondary-color);
  font-size: 0.9rem;
  padding: 0.5rem 1rem;
  
  &:hover {
    background: var(--dark-gray);
  }
`;

const ProjectForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEditMode = !!id;
  
  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    type: ProjectType;
    location: string;
    startDate: string;
    endDate: string;
    status: ProjectStatus;
    budget: {
      materials: number;
      labor: number;
      equipment: number;
      subContracts: number;
      others: number;
    };
    assignedStaff: AssignedStaff[];
    newStaff: {
      userId: string;
      role: UserRole;
    };
  }>({
    name: '',
    description: '',
    type: 'Building Works',
    location: '',
    startDate: '',
    endDate: '',
    status: 'Draft',
    budget: {
      materials: 0,
      labor: 0,
      equipment: 0,
      subContracts: 0,
      others: 0
    },
    assignedStaff: [],
    newStaff: {
      userId: '',
      role: 'Supervisor'
    }
  });
  
  const [errors, setErrors] = useState<{
    name?: string;
    location?: string;
    startDate?: string;
    budget?: string;
  }>({});
  
  const [loading, setLoading] = useState(isEditMode);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [projectError, setProjectError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const [staffMessage, setStaffMessage] = useState<string>('');
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Fetch users with proper error handling and retry mechanism
  const fetchUsers = async () => {
    setUsersLoading(true);
    setUsersError(null);
    
    try {
      const response = await apiService.getUsers({
        limit: 100 // Get all users for better selection
      });
      
      if (response.success && response.data) {
        setUsers(response.data);
        console.log('Users loaded successfully:', response.data.length);
      } else {
        throw new Error('Failed to load users');
      }
    } catch (error: any) {
      console.error('Error loading users:', error);
      setUsersError(error.message || 'Failed to load users. Please try again.');
      
      // Fallback to mock users only for development/testing
      if (process.env.NODE_ENV === 'development') {
        const mockUsers: User[] = [
          { id: '1', name: 'John Supervisor', email: 'john@example.com', role: 'Supervisor', status: 'Active', activated: true, passwordSet: true, createdAt: new Date(), updatedAt: new Date() },
          { id: '2', name: 'Jane Store-Keeper', email: 'jane@example.com', role: 'Store-Keeper (Logistics)', status: 'Active', activated: true, passwordSet: true, createdAt: new Date(), updatedAt: new Date() },
          { id: '3', name: 'Bob Cashier', email: 'bob@example.com', role: 'Cashier', status: 'Active', activated: true, passwordSet: true, createdAt: new Date(), updatedAt: new Date() },
          { id: '4', name: 'Alice Supervisor', email: 'alice@example.com', role: 'Supervisor', status: 'Active', activated: true, passwordSet: true, createdAt: new Date(), updatedAt: new Date() },
          { id: '5', name: 'Charlie Logistics', email: 'charlie@example.com', role: 'Store-Keeper (Logistics)', status: 'Active', activated: true, passwordSet: true, createdAt: new Date(), updatedAt: new Date() },
          { id: '6', name: 'David Cashier', email: 'david@example.com', role: 'Cashier', status: 'Active', activated: true, passwordSet: true, createdAt: new Date(), updatedAt: new Date() }
        ];
        setUsers(mockUsers);
        console.log('Using mock users for development');
      }
    } finally {
      setUsersLoading(false);
    }
  };
  
  // Filter users for project managers (Project Managers, MD, and TD)
  const projectManagers = users.filter((user: User) => 
    user.role === 'Project Manager' || user.role === 'MD' || user.role === 'TD'
  );
  
  // Filter users for staff assignment - show all active users with appropriate roles
  const availableStaff = users.filter((user: User) => 
    (user._id || user.id) && // Ensure user has a valid ID (handle both _id and id)
    user.status === 'Active' && // Only active users
    !formData.assignedStaff.some((staff: any) => staff.userId === (user._id || user.id)) &&
    (user.role === 'Supervisor' || 
     user.role === 'Store-Keeper (Logistics)' || 
     user.role === 'Cashier' ||
     user.role === 'Project Manager')
  );
  
  // Refresh users data
  const handleRefreshUsers = () => {
    setRefreshKey(prev => prev + 1);
    fetchUsers();
  };
  
  useEffect(() => {
    fetchUsers();
  }, [refreshKey]);
  
  useEffect(() => {
    const loadData = async () => {
      try {
        if (isEditMode && id) {
          console.log('Loading project data for editing, ID:', id);
          setLoading(true);
          setProjectError(null);
          
          // Load project data for editing (no authentication check)
          const projectResponse = await apiService.getProject(id);
          console.log('Project response:', projectResponse);
          
          if (!projectResponse.success || !projectResponse.data) {
            throw new Error('Invalid project data received');
          }
          
          const projectData = projectResponse.data;
          console.log('Project data:', projectData);
          
          // Validate required fields
          if (!projectData.name || !projectData.type || !projectData.location) {
            throw new Error('Project data is incomplete');
          }
          
          console.log('Setting form data with project data:', {
            name: projectData.name,
            description: projectData.description,
            type: projectData.type,
            location: projectData.location,
            budget: projectData.budget,
            assignedStaff: projectData.assignedStaff
          });
          
          setFormData({
            name: projectData.name,
            description: projectData.description || '',
            type: projectData.type,
            location: projectData.location,
            startDate: new Date(projectData.startDate).toISOString().split('T')[0],
            endDate: projectData.endDate ? new Date(projectData.endDate).toISOString().split('T')[0] : '',
            status: projectData.status || 'Draft',
            budget: {
              materials: projectData.budget?.materials || 0,
              labor: projectData.budget?.labor || 0,
              equipment: projectData.budget?.equipment || 0,
              subContracts: projectData.budget?.subContracts || 0,
              others: projectData.budget?.others || 0
            },
            assignedStaff: projectData.assignedStaff || [],
            newStaff: {
              userId: '',
              role: 'Supervisor'
            }
          });
          console.log('Form data set successfully');
        }
      } catch (error: any) {
        console.error('Error loading project data:', error);
        console.error('Error details:', {
          message: error.message,
          stack: error.stack,
          response: error.response
        });
        setProjectError(error.message || 'Failed to load project data');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [id, isEditMode]);
  
  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
    
    // Show warning when status is changed to Completed
    if (field === 'status' && value === 'Completed') {
      const hasAssignedStaff = formData.assignedStaff.length > 0;
      if (hasAssignedStaff) {
        alert('⚠️ Warning: Marking this project as "Completed" will release all assigned staff members for reassignment to other projects.');
      }
    }
  };
  
  const handleBudgetChange = (category: string, value: number) => {
    setFormData(prev => ({
      ...prev,
      budget: {
        ...prev.budget,
        [category]: value
      }
    }));
  };
  
  const calculateTotalBudget = () => {
    return Object.values(formData.budget).reduce((total, amount) => total + amount, 0);
  };
  
  const addStaff = () => {
    if (!formData.newStaff.userId) {
      return;
    }
    
    const selectedUser = users.find((u: User) => (u._id || u.id) === formData.newStaff.userId);
    if (!selectedUser || !(selectedUser._id || selectedUser.id)) {
      return;
    }
    
    const newStaffMember: AssignedStaff = {
      userId: formData.newStaff.userId,
      name: selectedUser.name,
      role: formData.newStaff.role,
      assignedAt: new Date(),
      assignedBy: user?.id || '',
      approved: false
    };
    
    setFormData(prev => ({
      ...prev,
      assignedStaff: [...prev.assignedStaff, newStaffMember],
      newStaff: {
        userId: '',
        role: 'Supervisor' as UserRole
      }
    }));
    
    // Show success message
    setStaffMessage(`${selectedUser.name} has been added as ${formData.newStaff.role}`);
    setTimeout(() => setStaffMessage(''), 3000);
  };
  
  const removeStaff = (userId: string) => {
    setFormData(prev => ({
      ...prev,
      assignedStaff: prev.assignedStaff.filter(staff => staff.userId !== userId)
    }));
  };
  
  const validateForm = () => {
    const newErrors: typeof errors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Project name is required';
    }
    
    if (!formData.location.trim()) {
      newErrors.location = 'Location is required';
    }
    
    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }
    
    if (calculateTotalBudget() <= 0) {
      newErrors.budget = 'Budget must be greater than zero';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e: React.FormEvent, isDraft: boolean = false) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitLoading(true);
    setSubmitError(null); // Clear previous errors
    setSubmitSuccess(null); // Clear previous success messages
    
    try {
      const projectData = {
        ...formData,
        status: isDraft ? 'Draft' : formData.status,
        budget: {
          ...formData.budget,
          total: calculateTotalBudget(),
          currency: 'TZS',
          approved: false
        },
        // Include creator role for automatic budget approval
        creatorRole: user?.role || 'Project Manager',
        managerRole: user?.role || 'Project Manager'
      };
      
      console.log('Submitting project:', projectData);
      
      if (isEditMode && id) {
        // Update existing project
        await apiService.updateProject(id, projectData);
        setSubmitSuccess(`Project "${formData.name}" has been updated successfully.`);
        
        // Navigate after a short delay to show success message
        setTimeout(() => {
          navigate('/projects');
        }, 1500);
      } else {
        // Create new project
        const response = await apiService.createProject(projectData);
        const projectId = response.data._id;
        
        // Notify HR (Admin) about new project creation for staff assignment
        if (!isDraft && (user?.role === 'MD' || user?.role === 'TD')) {
          NotificationService.notifyProjectCreated(projectId, formData.name);
        }
        setSubmitSuccess(`Project "${formData.name}" has been created successfully.`);
        
        // Navigate after a short delay to show success message
        setTimeout(() => {
          navigate('/projects');
        }, 1500);
      }
      
      // navigate('/projects'); // Removed immediate navigation
    } catch (error: any) {
      console.error('Error submitting project:', error);
      setSubmitError(error.message || 'Failed to save project. Please try again.');
    } finally {
      setSubmitLoading(false);
    }
  };
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-TZ', {
      style: 'currency',
      currency: 'TZS',
      minimumFractionDigits: 0
    }).format(amount);
  };
  
  // Add a helper to check if the field should be editable
  const canEditBudgetOnly = user?.role === 'Accountant' && isEditMode;
  
  if (loading) {
    return <LoadingSpinner>Loading project details...</LoadingSpinner>;
  }
  
  return (
    <div>
      <PageHeader>
        <h1 className="page-title">
          {isEditMode ? 'Edit Project' : 'Create New Project'}
        </h1>
        <FlexContainer gap="0.5rem">
          <OutlineButton as={Link} to="/projects">
            Cancel
          </OutlineButton>
        </FlexContainer>
      </PageHeader>
      
      {/* Users Loading/Error Section */}
      {usersError && (
        <ErrorMessage>
          <strong>Error loading users:</strong> {usersError}
          <Button 
            onClick={handleRefreshUsers} 
            style={{ marginLeft: '1rem', background: 'white', color: 'var(--danger-color)' }}
          >
            Retry
          </Button>
        </ErrorMessage>
      )}
      
      {/* Project Loading/Error Section */}
      {projectError && (
        <ErrorMessage>
          <strong>Error loading project:</strong> {projectError}
          <Button 
            onClick={() => window.location.reload()} 
            style={{ marginLeft: '1rem', background: 'white', color: 'var(--danger-color)' }}
          >
            Retry
          </Button>
        </ErrorMessage>
      )}
      
      {usersLoading && (
        <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--primary-color)' }}>
          Loading available users...
        </div>
      )}
      
      <form onSubmit={(e) => handleSubmit(e, false)}>
        <FormSection>
          <SectionTitle>Project Information</SectionTitle>
          <Card>
            <CardBody>
              <Grid columns="1fr 1fr" gap="1rem">
                <FormGroup>
                  <Label>Project Name *</Label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter project name"
                    disabled={canEditBudgetOnly}
                  />
                  {errors.name && <ErrorText>{errors.name}</ErrorText>}
                </FormGroup>
                
                <FormGroup>
                  <Label>Project Type</Label>
                  <Select
                    value={formData.type}
                    onChange={(e) => handleInputChange('type', e.target.value as ProjectType)}
                    disabled={canEditBudgetOnly}
                  >
                    <option value="Building Works">Building Works</option>
                    <option value="Civil Works">Civil Works</option>
                    <option value="Renovation Works">Renovation Works</option>
                    <option value="Structural Design">Structural Design</option>
                    <option value="Architectural Design">Architectural Design</option>
                    <option value="Supply of Building Materials">Supply of Building Materials</option>
                    <option value="Fabrication of Building Components & Hardware">Fabrication of Building Components & Hardware</option>
                  </Select>
                </FormGroup>
                
                <FormGroup>
                  <Label>Project Status</Label>
                  <Select
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value as ProjectStatus)}
                    disabled={canEditBudgetOnly}
                  >
                    <option value="Draft">Draft</option>
                    <option value="In-Progress">In-Progress</option>
                    <option value="On-Hold">On-Hold</option>
                    <option value="Completed">Completed</option>
                    <option value="Cancelled">Cancelled</option>
                  </Select>
                  {formData.status === 'Completed' && formData.assignedStaff.length > 0 && (
                    <div style={{ 
                      fontSize: '0.8rem', 
                      color: 'var(--warning-color)', 
                      marginTop: '0.5rem',
                      padding: '0.5rem',
                      backgroundColor: 'var(--light-gray)',
                      borderRadius: 'var(--border-radius)',
                      border: '1px solid var(--warning-color)'
                    }}>
                      ⚠️ When you save this project as "Completed", all assigned staff members will be automatically released and can be assigned to other projects.
                    </div>
                  )}
                </FormGroup>
                
                <FormGroup>
                  <Label>Location *</Label>
                  <Input
                    type="text"
                    value={formData.location}
                    onChange={(e) => handleInputChange('location', e.target.value)}
                    placeholder="Enter project location"
                    disabled={canEditBudgetOnly}
                  />
                  {errors.location && <ErrorText>{errors.location}</ErrorText>}
                </FormGroup>
                
                <FormGroup>
                  <Label>Start Date *</Label>
                  <Input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => handleInputChange('startDate', e.target.value)}
                    disabled={canEditBudgetOnly}
                  />
                  {errors.startDate && <ErrorText>{errors.startDate}</ErrorText>}
                </FormGroup>
                
                <FormGroup>
                  <Label>End Date (Optional)</Label>
                  <Input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => handleInputChange('endDate', e.target.value)}
                    disabled={canEditBudgetOnly}
                  />
                </FormGroup>
              </Grid>
              
              <FormGroup>
                <Label>Description</Label>
                <TextArea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Enter project description"
                  disabled={canEditBudgetOnly}
                />
              </FormGroup>
            </CardBody>
          </Card>
        </FormSection>
        
        <FormSection>
          <SectionTitle>Budget Allocation</SectionTitle>
          <BudgetGrid>
            <BudgetCard>
              <div className="budget-label">Materials</div>
              <Input
                type="number"
                className="budget-input"
                value={formData.budget.materials}
                onChange={e => handleBudgetChange('materials', Number(e.target.value))}
                disabled={!(user?.role === 'Accountant' && isEditMode) && !(!isEditMode)}
              />
            </BudgetCard>
            <BudgetCard>
              <div className="budget-label">Labor</div>
              <Input
                type="number"
                className="budget-input"
                value={formData.budget.labor}
                onChange={e => handleBudgetChange('labor', Number(e.target.value))}
                disabled={!(user?.role === 'Accountant' && isEditMode) && !(!isEditMode)}
              />
            </BudgetCard>
            <BudgetCard>
              <div className="budget-label">Equipment</div>
              <Input
                type="number"
                className="budget-input"
                value={formData.budget.equipment}
                onChange={e => handleBudgetChange('equipment', Number(e.target.value))}
                disabled={!(user?.role === 'Accountant' && isEditMode) && !(!isEditMode)}
              />
            </BudgetCard>
            <BudgetCard>
              <div className="budget-label">Sub-Contracts</div>
              <Input
                type="number"
                className="budget-input"
                value={formData.budget.subContracts}
                onChange={e => handleBudgetChange('subContracts', Number(e.target.value))}
                disabled={!(user?.role === 'Accountant' && isEditMode) && !(!isEditMode)}
              />
            </BudgetCard>
            <BudgetCard>
              <div className="budget-label">Others</div>
              <Input
                type="number"
                className="budget-input"
                value={formData.budget.others}
                onChange={e => handleBudgetChange('others', Number(e.target.value))}
                disabled={!(user?.role === 'Accountant' && isEditMode) && !(!isEditMode)}
              />
            </BudgetCard>
          </BudgetGrid>
          <TotalBudget>
            <div className="total-label">Total Budget</div>
            <div className="total-amount">{formatCurrency(
              formData.budget.materials +
              formData.budget.labor +
              formData.budget.equipment +
              formData.budget.subContracts +
              formData.budget.others
            )}</div>
          </TotalBudget>
        </FormSection>
        
        {!(user?.role === 'Accountant' && isEditMode) ? (
          <StaffSection>
            <SectionTitle>
              Staff Assignment
              <RefreshButton 
                type="button" 
                onClick={handleRefreshUsers}
                disabled={usersLoading}
                style={{ marginLeft: '1rem', fontSize: '0.8rem' }}
              >
                {usersLoading ? 'Loading...' : 'Refresh Users'}
              </RefreshButton>
            </SectionTitle>
            <Card>
              <CardBody>
                <AddStaffForm>
                  <div className="form-row">
                    <FormGroup>
                      <Label>Select Staff Member</Label>
                      <UserSelectContainer>
                        <Select
                          value={formData.newStaff.userId}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            newStaff: { ...prev.newStaff, userId: e.target.value }
                          }))}
                          disabled={usersLoading}
                        >
                          <option key="default" value="">Select staff member</option>
                          {availableStaff.length > 0 ? (
                            availableStaff.map((staff, index) => (
                              <option key={`staff-${staff._id || staff.id || index}`} value={staff._id || staff.id}>
                                {staff.name} ({staff.role})
                              </option>
                            ))
                          ) : (
                            <option value="" disabled>
                              {usersLoading ? 'Loading users...' : 'No available staff members'}
                            </option>
                          )}
                        </Select>
                        {formData.newStaff.userId && (
                          <div className="user-info">
                            <span className="user-role">
                              {users.find(u => (u._id || u.id) === formData.newStaff.userId)?.role}
                            </span>
                            <span>
                              {users.find(u => (u._id || u.id) === formData.newStaff.userId)?.email}
                            </span>
                          </div>
                        )}
                      </UserSelectContainer>
                    </FormGroup>
                    
                    <FormGroup>
                      <Label>Role in Project</Label>
                      <Select
                        value={formData.newStaff.role}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          newStaff: { ...prev.newStaff, role: e.target.value as UserRole }
                        }))}
                      >
                        <option value="Supervisor">Supervisor</option>
                        <option value="Store-Keeper (Logistics)">Store-Keeper (Logistics)</option>
                        <option value="Cashier">Cashier</option>
                        <option value="Project Manager">Project Manager</option>
                      </Select>
                    </FormGroup>
                    
                    <Button 
                      type="button" 
                      onClick={addStaff} 
                      disabled={!formData.newStaff.userId || usersLoading}
                    >
                      Add Staff
                    </Button>
                  </div>
                  
                  {staffMessage && (
                    <SuccessMessage>
                      {staffMessage}
                    </SuccessMessage>
                  )}
                  
                  {availableStaff.length === 0 && !usersLoading && (
                    <div style={{ 
                      marginTop: '1rem', 
                      padding: '0.75rem', 
                      backgroundColor: 'var(--warning-color)', 
                      color: 'white', 
                      borderRadius: 'var(--border-radius)',
                      textAlign: 'center'
                    }}>
                      No available staff members found. Please ensure users are active and have the required roles.
                    </div>
                  )}
                </AddStaffForm>
                
                <StaffSection>
                  {formData.assignedStaff.map((staff, index) => (
                    <StaffCard key={`assigned-${staff.userId || index}`}>
                      <div className="staff-header">
                        <div>
                          <strong>{staff.name}</strong>
                          <div style={{ color: 'var(--dark-gray)', fontSize: '0.9rem' }}>
                            {staff.role}
                          </div>
                        </div>
                        <RemoveButton type="button" onClick={() => removeStaff(staff.userId)}>
                          Remove
                        </RemoveButton>
                      </div>
                    </StaffCard>
                  ))}
                  
                  {formData.assignedStaff.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--dark-gray)' }}>
                      No staff assigned yet. Add staff members using the form above.
                    </div>
                  )}
                </StaffSection>
              </CardBody>
            </Card>
          </StaffSection>
        ) : (
          <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--dark-gray)' }}>
            Staff assignment is not available for Accountant role in edit mode.
          </div>
        )}
        
        <Card>
          <CardFooter>
            <FlexContainer gap="1rem">
              <Button
                type="button"
                onClick={(e) => handleSubmit(e, true)}
                disabled={submitLoading}
                style={{ backgroundColor: 'var(--medium-gray)' }}
              >
                {submitLoading ? 'Saving...' : 'Save as Draft'}
              </Button>
              
              <Button
                type="submit"
                disabled={submitLoading}
              >
                {submitLoading ? 'Submitting...' : isEditMode ? 'Update Project' : 'Create Project'}
              </Button>
              
              <OutlineButton type="button" as={Link} to="/projects">
                Cancel
              </OutlineButton>
            </FlexContainer>
          </CardFooter>
        </Card>
      </form>
      
      {submitError && (
        <ErrorMessage>
          <strong>Error:</strong> {submitError}
        </ErrorMessage>
      )}

      {submitSuccess && (
        <SuccessMessage>
          <strong>Success:</strong> {submitSuccess}
        </SuccessMessage>
      )}
      
      <div style={{ textAlign: 'right', padding: '1rem 0', color: 'var(--dark-gray)', fontSize: '0.8rem' }}>
        Developed by Sandfish
      </div>
    </div>
  );
};

export default ProjectForm; 